import apiService from './apiService';

/**
 * Staff attendance API. Kept in its own file (paths included) rather than in
 * apiService / API_ENDPOINTS so it adds nothing to the files feat/brand-branches
 * rewrites.
 *
 * `studioId` is required for super admins and ignored by the server for studio
 * logins (their studio comes from the token). Writes also send it as the
 * `x-studio-id` header so they stay pinned to one location once brand-branches
 * adds the location switcher.
 */
const BASE = '/api/v1/staff-attendance';

export interface ShiftDay {
  weekday: number; // 0 = Sunday
  start: string; // HH:mm
  end: string; // HH:mm
}

export interface AttendancePunch {
  _id: string;
  studioId: string;
  date: string;
  inAt: string;
  outAt: string | null;
  status: 'open' | 'closed' | 'missed_out';
  durationMinutes: number | null;
  inDistanceMeters: number | null;
  outDistanceMeters: number | null;
  edited: { by: string; at: string; note: string | null } | null;
}

export interface AttendanceDay {
  date: string;
  weekday: number;
  status: 'present' | 'absent' | 'off' | 'upcoming';
  shift: { start: string; end: string } | null;
  totalMinutes: number;
  firstIn: string | null;
  lastOut: string | null;
  lateMinutes: number;
  earlyArrivalMinutes: number;
  leftEarlyMinutes: number;
  overtimeMinutes: number;
  openNow: boolean;
  missedPunchOut: boolean;
  punches: AttendancePunch[];
}

export interface AttendanceStaffRow {
  studioUserId: string;
  name: string;
  role: 'manager' | 'instructor' | string;
  isActive: boolean;
  removed: boolean;
  instructorId: string | null;
  weeklyShift: ShiftDay[];
  totals: {
    presentDays: number;
    absentDays: number;
    lateDays: number;
    earlyArrivalDays: number;
    leftEarlyDays: number;
    overtimeDays: number;
    missedPunchOuts: number;
    totalMinutes: number;
    lateMinutes: number;
    earlyArrivalMinutes: number;
    leftEarlyMinutes: number;
    overtimeMinutes: number;
    scheduledMinutes: number;
  };
  days: AttendanceDay[];
}

export interface AttendanceSettings {
  studioId: string;
  enabled: boolean;
  radiusMeters: number;
  graceMinutes: number;
  updatedAt?: string | null;
  updatedBy?: { name: string } | null;
  locationSet?: boolean;
}

export interface AttendanceReport {
  studioId: string;
  from: string;
  to: string;
  today: string;
  settings: AttendanceSettings;
  instructorHasNoLogin: boolean;
  staff: AttendanceStaffRow[];
}

export interface AttendanceStaffMember {
  studioUserId: string;
  name: string;
  role: string;
  email: string;
  phone: string | null;
  isActive: boolean;
  instructorId: string | null;
  days: ShiftDay[];
  suggestedShift: { start: string; end: string } | null;
}

const pin = (studioId?: string) => (studioId ? { headers: { 'x-studio-id': studioId } } : {});

export const attendanceApi = {
  getReport(params: {
    from: string;
    to: string;
    studioId?: string;
    instructorId?: string;
    studioUserId?: string;
  }): Promise<AttendanceReport> {
    return apiService.get(`${BASE}/admin/studio/report`, { params });
  },

  getStaff(studioId?: string): Promise<{ settings: AttendanceSettings; staff: AttendanceStaffMember[] }> {
    return apiService.get(`${BASE}/admin/studio/staff`, { params: studioId ? { studioId } : {} });
  },

  setShift(studioUserId: string, days: ShiftDay[], studioId?: string) {
    return apiService.put(
      `${BASE}/admin/studio/shift/${studioUserId}`,
      { days, ...(studioId ? { studioId } : {}) },
      pin(studioId),
    );
  },

  editPunch(punchId: string, body: { inAt?: string; outAt?: string; note?: string }, studioId?: string) {
    return apiService.patch(`${BASE}/admin/studio/punch/${punchId}`, body, {
      params: studioId ? { studioId } : {},
      ...pin(studioId),
    });
  },

  /** Super admin only. */
  /** Studio-scoped read: owners/managers get their own studio, admins pass one. */
  getStudioSettings(studioId?: string): Promise<AttendanceSettings> {
    return apiService.get(`${BASE}/admin/studio/settings`, { params: studioId ? { studioId } : {} });
  },

  getSettings(studioId: string): Promise<AttendanceSettings> {
    return apiService.get(`${BASE}/admin/settings/${studioId}`);
  },

  /** Super admin only. */
  updateSettings(
    studioId: string,
    body: Partial<Pick<AttendanceSettings, 'enabled' | 'radiusMeters' | 'graceMinutes'>>,
  ): Promise<AttendanceSettings> {
    return apiService.put(`${BASE}/admin/settings/${studioId}`, body);
  },
};
